<template>
  <v-sheet
    v-if="visible"
    class="request-lifecycle-banner pa-3 mb-2"
    rounded
    elevation="0"
    :class="bannerClass"
  >
    <div class="rlb__message-row">
      <v-icon small class="mt-1 flex-shrink-0" :color="iconColor">
        mdi-information-outline
      </v-icon>
      <div
        class="rlb__message text-body-2"
        :class="messageToneClass"
      >
        {{ displaySummary }}
      </div>
    </div>
    <div
      v-if="blockedMode || readOnly || phaseLabel"
      class="rlb__chips-row d-flex flex-wrap align-center mt-2"
      style="gap: 8px"
    >
      <span
        v-if="blockedMode"
        class="rlb-pill rlb-pill--blocked flex-shrink-0"
      >
        بدون فتح التفاصيل
      </span>
      <span
        v-else-if="readOnly"
        class="rlb-pill rlb-pill--readonly flex-shrink-0"
      >
        عرض فقط
      </span>
      <span
        v-if="phaseLabel"
        class="rlb-pill rlb-pill--phase flex-shrink-0"
      >
        {{ phaseLabel }}
      </span>
    </div>
  </v-sheet>
</template>

<script>
export default {
  name: "RequestLifecycleBanner",
  props: {
    summaryAr: {
      type: String,
      default: "",
    },
    canEdit: {
      type: [Boolean, Number],
      default: true,
    },
    phase: {
      type: String,
      default: "",
    },
    blockedDetail: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    blockedMode() {
      return this.blockedDetail === true;
    },
    trimmedSummary() {
      if (this.summaryAr == null || this.summaryAr === "") {
        return "";
      }
      return String(this.summaryAr).trim();
    },
    readOnly() {
      if (this.blockedMode) return false;
      const v = this.canEdit;
      return v === false || v === 0 || v === "0";
    },
    displaySummary() {
      if (this.trimmedSummary) {
        return this.trimmedSummary;
      }
      if (this.blockedMode) {
        return (
          this.phaseHintAr ||
          "يمكنك الاطلاع على الموجز هنا؛ صفحة التفاصيل غير متاحة لحسابك."
        );
      }
      if (this.readOnly) {
        return (
          this.phaseHintAr ||
          "لا يمكن تعديل هذا الطلب بحسابك الحالي."
        );
      }
      return "";
    },
    messageToneClass() {
      if (this.blockedMode) {
        return this.isDark ? "white--text" : "grey--text text--darken-4";
      }
      if (this.readOnly) {
        return this.isDark ? "white--text" : "grey--text text--darken-4";
      }
      return this.isDark ? "grey--text text--lighten-2" : "grey--text text--darken-3";
    },
    visible() {
      if (this.blockedMode) {
        return (
          this.displaySummary !== "" ||
          this.trimmedSummary !== "" ||
          this.phaseLabel !== ""
        );
      }
      return (
        this.displaySummary !== "" ||
        this.readOnly ||
        this.phaseLabel !== ""
      );
    },
    isDark() {
      return this.$store.getters.isDark;
    },
    bannerClass() {
      if (this.blockedMode) {
        return this.isDark ? "grey darken-4" : "deep-orange lighten-5";
      }
      if (this.readOnly) {
        return this.isDark ? "grey darken-3" : "amber lighten-4";
      }
      return this.isDark ? "grey darken-4" : "blue-grey lighten-5";
    },
    iconColor() {
      if (this.blockedMode) return "deep-orange darken-2";
      return this.readOnly ? "warning darken-1" : "primary";
    },
    phaseLabel() {
      const p = (this.phase && String(this.phase).trim()) || "";
      if (!p || p === "unknown") {
        return "";
      }
      const map = {
        admin_side: "سلة المبيعات / الإدارة",
        cs_side: "خدمة العملاء",
        agent_side: "الاستشاري",
        unassigned: "غير مُسند",
      };
      return map[p] || p;
    },
    phaseHintAr() {
      const p = (this.phase && String(this.phase).trim()) || "";
      if (p === "admin_side") {
        return "الطلب في سلة المبيعات / الإدارة حاليًا (تم إرساله من خدمة العملاء).";
      }
      if (p === "agent_side") {
        return "الطلب لدى الاستشاري حاليًا.";
      }
      if (p === "cs_side") {
        return "الطلب لدى خدمة العملاء حاليًا.";
      }
      if (p === "unassigned") {
        return "الطلب غير مُسند حاليًا.";
      }
      return "";
    },
  },
};
</script>

<style scoped>
.rlb__message-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.rlb__message {
  flex: 1 1 0;
  min-width: 0;
  line-height: 1.55;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.rlb__chips-row {
  margin-inline-start: 28px;
}

.rlb-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  border-radius: 999px;
  border: 1px solid transparent;
  box-sizing: border-box;
}

.rlb-pill--blocked {
  color: #bf360c !important;
  background: rgba(191, 54, 12, 0.1);
  border-color: rgba(191, 54, 12, 0.45);
}

.rlb-pill--readonly {
  color: #f57f17 !important;
  background: rgba(245, 127, 23, 0.12);
  border-color: rgba(245, 127, 23, 0.5);
}

.rlb-pill--phase {
  color: var(--v-primary-base, #451646) !important;
  background: rgba(69, 22, 70, 0.08);
  border-color: rgba(69, 22, 70, 0.45);
}

.theme--dark .rlb-pill--blocked {
  color: #ffab91 !important;
  background: rgba(255, 171, 145, 0.12);
  border-color: rgba(255, 171, 145, 0.4);
}

.theme--dark .rlb-pill--readonly {
  color: #ffe082 !important;
  background: rgba(255, 224, 130, 0.12);
  border-color: rgba(255, 224, 130, 0.45);
}

.theme--dark .rlb-pill--phase {
  color: #ce93d8 !important;
  background: rgba(206, 147, 216, 0.12);
  border-color: rgba(206, 147, 216, 0.45);
}
</style>
